import { memo, type ReactNode, useCallback, useState } from 'react';

import { useAnalyticsUtilities } from '@air/analytics';
import { FlashFilledIcon } from '@air/next-icons';
import { Popover } from '@air/primitive-popover';
import { useAirModal } from '@air/provider-modal';

import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { UPSELL_FLASH_ICON_BUTTON } from '~/constants/testIDs';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

type UpsellFlashIconButtonProps = {
  children: ReactNode;
};

export const UpsellFlashIconButton = memo<UpsellFlashIconButtonProps>(({ children }) => {
  const [showPlansModal] = useAirModal(PlansModal);
  const [isOpen, setIsOpen] = useState(false);
  const { track } = useAnalyticsUtilities();
  const { data: subscription } = useSubscriptionContext();

  const handleUpgradeClick = useCallback(() => {
    // Track upgrade button clicked with current plan
    track('Upgrade Button Clicked', {
      plan: subscription?.plan || 'unknown',
    });

    showPlansModal();
  }, [track, subscription?.plan, showPlansModal]);

  return (
    <Popover
      side="top"
      align="start"
      data-theme="dark"
      className="max-w-80 border-0"
      open={isOpen}
      onOpenChange={setIsOpen}
      trigger={
        <button
          type="button"
          data-testid={UPSELL_FLASH_ICON_BUTTON}
          className="flex items-center justify-center"
          onClick={() => setIsOpen(!isOpen)}
        >
          <FlashFilledIcon className="size-3.5 cursor-pointer text-blue-9 hover:text-blue-10" />
        </button>
      }
    >
      <div className="flex items-center gap-3 p-2">
        <FlashFilledIcon className="size-8 text-blue-9" />
        <div className="text-12 text-grey-12">
          {children}{' '}
          <button
            onClick={handleUpgradeClick}
            className="cursor-pointer text-grey-12 underline hover:text-grey-11"
            type="button"
          >
            Click to upgrade.
          </button>
        </div>
      </div>
    </Popover>
  );
});

UpsellFlashIconButton.displayName = 'UpsellFlashIconButton';
